import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { BackButton } from "../../components/BackButton";
import { requestOtp } from "../../api/auth";
import { ApiError } from "../../api/client";

/**
 * Forgot-password step 1 — enter email to get an OTP.
 *
 * The OTP verifies email ownership (the same credential as signup), then
 * the student sets a new password. No auth required — the whole point is
 * that they cannot log in.
 */
export function ResetPhoneScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const emailError = submitted && !emailLooksValid ? "Enter a valid email address." : null;

  const submit = async () => {
    setSubmitted(true);
    if (!emailLooksValid || sending) return;

    setSending(true);
    setFailure(null);

    try {
      // A reset code: its own kind, so it can't be used to sign up, and it
      // lasts 15 minutes (time to enter it and choose a new password).
      const { resendAfterSeconds } = await requestOtp(email.trim().toLowerCase(), {
        purpose: "password_reset",
      });

      navigate("/verify", {
        state: { email: email.trim().toLowerCase(), resendAfterSeconds, purpose: "reset" },
      });
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : "Couldn't send the code. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/login" />

      <h1 className="m-0 pt-4 font-[family-name:var(--font-display)] text-[22px] font-bold leading-[1.2]">
        Reset your password
      </h1>
      <p className="m-0 pt-2 text-[13.5px] leading-[1.55] text-text-muted">
        Enter your email and we'll send you a code to verify it's you.
      </p>

      <div className="pt-6">
        <Field label="Email" htmlFor="reset-email" error={emailError}>
          <div
            className={`${inputShell} ${(emailError ?? failure) ? "border-negative" : "border-border-strong"}`}
          >
            <input
              id="reset-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              aria-invalid={Boolean(emailError ?? failure)}
              className="w-full bg-transparent text-[15px] text-text outline-none placeholder:text-text-muted"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        </Field>
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-8">
        {failure ? (
          <p role="alert" className="m-0 text-center text-[12.5px] text-negative">
            {failure}
          </p>
        ) : null}
        <Button onClick={() => void submit()} disabled={(submitted && !emailLooksValid) || sending}>
          {sending ? "Sending code…" : "Send code"}
        </Button>
      </div>
    </div>
  );
}
